import React, { createContext, useContext, ReactNode } from "react";
// Asume que cada import es del tipo TranslationModule como se define abajo
import { commonTranslations } from "./translations/common";
import { homeTranslations } from "./translations/home";
import { toolsTranslations } from "./translations/tools";
import { aboutTranslations } from "./translations/about";
import { contactTranslations } from "./translations/contact";
import { resourcesTranslations } from "./translations/resources";
import { footerTranslations } from "./translations/footer";
import { cookieConsentTranslations } from "./translations/cookieConsent";
import { compoundTranslations } from "./translations/compound";
import { compoundInterestArticleTranslations } from "./translations/compoundInterestArticle";
import { retirementPlanningArticleTranslations } from "./translations/retirementPlanningArticle";
import { riskReturnArticleTranslations } from "./translations/riskReturnArticle";
import { emergencyFundArticleTranslations } from "./translations/emergencyFundArticle";
import { investingMistakesArticleTranslations } from "./translations/investingMistakesArticle";
import { rentingVsBuyingArticleTranslations } from "./translations/rentingVsBuyingArticle";
import { inflationArticleTranslations } from "./translations/inflationArticle";
import { inflationSavingsStrategiesTranslations } from "./translations/inflationSavingsStrategiesArticle";
import { cryptoArticleTranslations } from "./translations/cryptoArticle";
import { monthlySavingChallengesArticleTranslations } from "./translations/monthlySavingChallengesArticle";
import { impulseBuyingArticleTranslations } from "./translations/impulseBuyingArticle";
import { mortgageRenegotiationArticleTranslations } from "./translations/mortgageRenegotiationArticle";
import { bnplArticleTranslations } from "./translations/bnplArticle";
import { aiFinanceArticleTranslations } from "./translations/aiFinanceArticle";
import { aiInvestmentArticleTranslations } from "./translations/aiInvestmentArticle";
import { cryptoTaxGuideArticleTranslations } from "./translations/cryptoTaxGuideArticle";
import { investingWith100ArticleTranslations } from "./translations/investingWith100Article";
import { startInvestingArticleTranslations } from "./translations/startInvestingArticle";
import { etfIndexFundsArticleTranslations } from "./translations/etfIndexFundsArticle";
import { esgGreenwashingArticleTranslations } from "./translations/esgGreenwashingArticle";
import { digitalScamsArticleTranslations } from "./translations/digitalScamsArticle";
import { selfEmployedFinancesArticleTranslations } from "./translations/selfEmployedFinancesArticle";
import { salaryNegotiationArticleTranslations } from "./translations/salaryNegotiationArticle";
import { salaryNegotiationWorthArticleTranslations } from "./translations/salaryNegotiationWorthArticle";
import { digitalNomadTaxGuideArticleTranslations } from "./translations/digitalNomadTaxGuideArticle";
import { financialIndependenceRealisticPlanArticleTranslations } from "./translations/financialIndependenceRealisticPlanArticle";
import { roboadvisorsVsHumanAdvisorsArticleTranslations } from "./translations/roboadvisorsVsHumanAdvisorsArticle";
import { utilityBillsArticleTranslations } from "./translations/utilityBillsArticle";
import { irpfGuideArticleTranslations } from "./translations/irpfGuideArticle";
import { financialPlanningBeforeChildrenTranslations } from "./translations/financialPlanningBeforeChildrenArticle";
import { lowSalarySavingStrategiesArticleTranslations } from "./translations/lowSalarySavingStrategiesArticle";
import { couplesFinanceTranslations } from "./translations/couplesFinanceArticle";
import { firstSalaryArticleTranslations } from "./translations/firstSalaryArticle";
import { automateFinancesArticleTranslations } from "./translations/automateFinancesArticle";
import { onePercentRuleArticleTranslations } from "./translations/onePercentRuleArticle";
import { fearOfInvestingArticleTranslations } from "./translations/fearOfInvestingArticle";
import { whyTradersLoseMoneyArticleTranslations } from "./translations/whyTradersLoseMoneyArticle";
import { realCostNeverInvestingArticleTranslations } from "./translations/realCostNeverInvestingArticle";
import { invisibleExpensesArticleTranslations } from "./translations/invisibleExpensesArticle";
import { lifestyleInflationArticleTranslations } from "./translations/lifestyleInflationArticle";
import { preparingFinancesBeforeParenthoodTranslations } from "./translations/preparingFinancesBeforeParenthoodArticle";
import { dcaVsLumpSumArticleTranslations } from "./translations/dcaVsLumpSumArticle";
import { howMuchToRetireArticleTranslations } from "./translations/howMuchToRetireArticle";
import { dividendReinvestmentArticleTranslations } from "./translations/dividendReinvestmentArticle";
import { beforeQuittingJobArticleTranslations } from "./translations/beforeQuittingJobArticle";
import { metaTranslations } from "./translations/meta";
import { cookiesTranslations } from './translations/cookies';
import { termsTranslations } from './translations/terms';
import { privacyTranslations } from './translations/privacy';
import { chatTranslations } from './translations/chat';
import { chartsTranslations } from './translations/charts';
import { imagesTranslations } from './translations/images';

type Locale = "en" | "es" | "de" | "fr" | "hi";

// NUEVA DEFINICIÓN PARA VALORES DE TRADUCCIÓN ANIDADOS
// Un valor de traducción puede ser un string, un array de strings, o un objeto que a su vez contiene más traducciones.
export type TranslationValue = string | string[] | { [key: string]: TranslationValue };

// SingleLocaleTranslations ahora usa TranslationValue
export type SingleLocaleTranslations = Record<string, TranslationValue>;

// Tipo para un módulo de traducción completo (como commonTranslations)
// que contiene todos los idiomas para ese módulo.
export type TranslationModule = Record<Locale, SingleLocaleTranslations>;

// Merge all translations by locale
const mergeTranslations = (locale: Locale): SingleLocaleTranslations => {
  const merged: SingleLocaleTranslations = {};

  // Asegúrate de que cada elemento aquí sea del tipo TranslationModule
  // TypeScript ahora debería aceptar esto con la nueva definición de SingleLocaleTranslations
  const allTranslationModules: TranslationModule[] = [
    commonTranslations,
    homeTranslations,
    toolsTranslations,
    aboutTranslations,
    contactTranslations,
    footerTranslations,
    cookieConsentTranslations,
    resourcesTranslations,
    compoundTranslations,
    compoundInterestArticleTranslations,
    retirementPlanningArticleTranslations,
    emergencyFundArticleTranslations,
    riskReturnArticleTranslations,
    investingMistakesArticleTranslations,
    investingWith100ArticleTranslations,
    startInvestingArticleTranslations,
    rentingVsBuyingArticleTranslations,
    inflationArticleTranslations,
    inflationSavingsStrategiesTranslations,
    cryptoArticleTranslations,
    cryptoTaxGuideArticleTranslations,
    monthlySavingChallengesArticleTranslations,
    impulseBuyingArticleTranslations,
    mortgageRenegotiationArticleTranslations,
    bnplArticleTranslations,
    aiFinanceArticleTranslations,
    aiInvestmentArticleTranslations,
    etfIndexFundsArticleTranslations,
    esgGreenwashingArticleTranslations,
    digitalScamsArticleTranslations,
    selfEmployedFinancesArticleTranslations,
    salaryNegotiationArticleTranslations,
    salaryNegotiationWorthArticleTranslations,
    digitalNomadTaxGuideArticleTranslations,
    financialIndependenceRealisticPlanArticleTranslations,
    roboadvisorsVsHumanAdvisorsArticleTranslations,
    utilityBillsArticleTranslations,
    irpfGuideArticleTranslations,
    financialPlanningBeforeChildrenTranslations,
    lowSalarySavingStrategiesArticleTranslations,
    couplesFinanceTranslations,
    firstSalaryArticleTranslations,
    automateFinancesArticleTranslations,
    onePercentRuleArticleTranslations,
    fearOfInvestingArticleTranslations,
    whyTradersLoseMoneyArticleTranslations,
    realCostNeverInvestingArticleTranslations,
    invisibleExpensesArticleTranslations,
    lifestyleInflationArticleTranslations,
    preparingFinancesBeforeParenthoodTranslations,
    dcaVsLumpSumArticleTranslations,
    howMuchToRetireArticleTranslations,
    dividendReinvestmentArticleTranslations,
    beforeQuittingJobArticleTranslations,
    fearOfInvestingArticleTranslations,
    dividendReinvestmentArticleTranslations,
    metaTranslations,
    cookiesTranslations,
    termsTranslations,
    privacyTranslations,
    chatTranslations,
    chartsTranslations,
    imagesTranslations,
  ];

  for (const transModule of allTranslationModules) {
    if (transModule && transModule[locale]) {
      // Object.assign debería funcionar bien con la estructura anidada
      // ya que copia propiedades superficialmente. La estructura interna
      // de los valores de propiedad se mantiene.
      Object.assign(merged, transModule[locale]);
    }
  }
  return merged;
};

// Tipo para el objeto principal de todas las traducciones fusionadas
type AllTranslations = Record<Locale, SingleLocaleTranslations>;

const translations: AllTranslations = {
  en: mergeTranslations("en"),
  es: mergeTranslations("es"),
  de: mergeTranslations("de"),
  fr: mergeTranslations("fr"),
  hi: mergeTranslations("hi"),
};

interface I18nContextType {
  t: (key: string) => string; // La función t todavía devuelve un string
  locale: Locale;
}

const I18nContext = createContext<I18nContextType | null>(null);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};

interface I18nProviderProps {
  children: ReactNode;
  locale: Locale;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({
  children,
  locale,
}) => {
  const t = (key: string): string => {
    const keys = key.split(".");
    const currentLocaleTranslations: SingleLocaleTranslations | undefined =
      translations[locale];

    if (!currentLocaleTranslations) {
      // Esto no debería pasar si locale es válido y translations está construido.
      return key;
    }

    // Empezamos con el objeto de traducciones del locale actual.
    // 'value' puede ser un string, array de strings, o un objeto anidado a medida que iteramos.
    let value: TranslationValue | undefined = currentLocaleTranslations;

    for (const k of keys) {
      // Si 'value' es un array, intentamos acceder por índice numérico
      if (Array.isArray(value)) {
        const index = parseInt(k, 10);
        if (!isNaN(index) && index >= 0 && index < value.length) {
          value = value[index];
          continue;
        }
        return key; // Si no es un índice válido, devolvemos la clave original
      }

      // Si en algún punto 'value' no es un objeto, o es null, o la clave 'k' no existe,
      // no podemos seguir navegando.
      if (
        typeof value !== "object" ||
        value === null ||
        !Object.prototype.hasOwnProperty.call(value, k)
      ) {
        return key; // Devuelve la clave original como fallback
      }
      value = (value as { [key: string]: TranslationValue })[k]; // Navega al siguiente nivel
    }

    // Al final, si 'value' es un string, esa es nuestra traducción.
    // Si es un array de strings, lo convertimos a string con comas.
    // Si es un objeto (porque la clave era demasiado corta), devuelve la clave original.
    if (typeof value === "string") {
      return value;
    } else if (Array.isArray(value)) {
      return value.join(", ");
    } else {
      return key;
    }
  };

  return (
    <I18nContext.Provider value={{ t, locale }}>
      {children}
    </I18nContext.Provider>
  );
};